import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

// ─── Transliteration Engine ──────────────────────────────────────────────────

const VOWELS = [
  ['aa', { f: 'आ', m: 'ा' }], ['AA', { f: 'आ', m: 'ा' }],
  ['ii', { f: 'ई', m: 'ी' }], ['ee', { f: 'ई', m: 'ी' }],
  ['uu', { f: 'ऊ', m: 'ू' }], ['oo', { f: 'ऊ', m: 'ू' }],
  ['ai', { f: 'ऐ', m: 'ै' }],
  ['au', { f: 'औ', m: 'ौ' }], ['aw', { f: 'औ', m: 'ौ' }],
  ['ri', { f: 'ऋ', m: 'ृ' }],
  ['A',  { f: 'आ', m: 'ा' }],
  ['I',  { f: 'ई', m: 'ी' }],
  ['U',  { f: 'ऊ', m: 'ू' }],
  ['E',  { f: 'ए', m: 'े' }],
  ['O',  { f: 'ओ', m: 'ो' }],
  ['a',  { f: 'अ', m: '' }],
  ['i',  { f: 'इ', m: 'ि' }],
  ['u',  { f: 'उ', m: 'ु' }],
  ['e',  { f: 'ए', m: 'े' }],
  ['o',  { f: 'ओ', m: 'ो' }],
];

const CONS = [
  ['ksh', 'क्ष'], ['gya', 'ज्ञ'], ['chh', 'छ'],
  ['kh', 'ख'], ['gh', 'घ'], ['ch', 'च'], ['jh', 'झ'],
  ['Th', 'ठ'], ['Dh', 'ढ'], ['th', 'थ'], ['dh', 'ध'],
  ['ph', 'फ'], ['bh', 'भ'], ['sh', 'श'], ['Sh', 'ष'],
  ['ny', 'ञ'], ['ng', 'ङ'],
  ['k', 'क'], ['g', 'ग'], ['c', 'क'], ['j', 'ज'],
  ['T', 'ट'], ['D', 'ड'], ['N', 'ण'], ['R', 'ड़'],
  ['t', 'त'], ['d', 'द'], ['n', 'न'],
  ['p', 'प'], ['b', 'ब'], ['m', 'म'],
  ['y', 'य'], ['r', 'र'], ['l', 'ल'], ['L', 'ळ'],
  ['v', 'व'], ['w', 'व'],
  ['s', 'स'], ['S', 'श'], ['h', 'ह'],
  ['f', 'फ'], ['z', 'ज़'], ['x', 'क्ष'],
  ['q', 'क'],
];

function transliterateWord(roman) {
  if (!roman) return '';
  let result = '';
  let i = 0;
  let prevWasCons = false;

  while (i < roman.length) {
    if (roman[i] === 'M') {
      result += 'ं';
      prevWasCons = false;
      i++;
      continue;
    }
    if (roman[i] === 'H' && i === roman.length - 1) {
      result += 'ः';
      prevWasCons = false;
      i++;
      continue;
    }

    let vm = null, vl = 0;
    for (const [seq, v] of VOWELS) {
      if (roman.substr(i, seq.length) === seq) { vm = v; vl = seq.length; break; }
    }

    let cm = null, cl = 0;
    for (const [seq, c] of CONS) {
      if (roman.substr(i, seq.length) === seq) { cm = c; cl = seq.length; break; }
    }

    if (vm !== null) {
      result += prevWasCons ? vm.m : vm.f;
      prevWasCons = false;
      i += vl;
    } else if (cm !== null) {
      if (prevWasCons) result += '्';
      result += cm;
      prevWasCons = true;
      i += cl;
    } else {
      result += roman[i];
      prevWasCons = false;
      i++;
    }
  }
  return result;
}

function escHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function buildOutputHtml(text, overrides) {
  if (!text.trim()) return '<span class="ht-placeholder">Hindi will appear here…</span>';
  return text.replace(/([^\s\n]+)|(\s+|\n)/g, (_, word, space) => {
    if (space) return space.replace(/\n/g, '<br>');
    const key = word.toLowerCase();
    const hindi = overrides.get(key) || transliterateWord(word);
    return `<span class="ht-word" data-roman="${escHtml(word)}">${escHtml(hindi)}</span>`;
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

const HindiTypingPage = () => {
  const [inputText, setInputText] = useState('');
  const [outputHtml, setOutputHtml] = useState('<span class="ht-placeholder">Hindi will appear here…</span>');
  const [charCount, setCharCount] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState('0s');
  const [wpm, setWpm] = useState(0);
  const [suggestions, setSuggestions] = useState(null); // { roman, list }
  const [copyLabel, setCopyLabel] = useState('Copy');

  const wordOverrides = useRef(new Map());
  const startTime = useRef(null);
  const statsInterval = useRef(null);
  const debounceTimer = useRef(null);
  const lastFetchedWord = useRef('');
  const inputRef = useRef(null);
  const outputRef = useRef(null);

  const updateStats = useCallback((text) => {
    const chars = text.length;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    setCharCount(chars);
    setWordCount(words);
    if (startTime.current) {
      const secs = Math.floor((Date.now() - startTime.current) / 1000);
      const mins = secs / 60;
      setTimeElapsed(secs + 's');
      setWpm(mins > 0 ? Math.round(words / mins) : 0);
    }
  }, []);

  useEffect(() => {
    return () => {
      clearInterval(statsInterval.current);
      clearTimeout(debounceTimer.current);
    };
  }, []);

  const getCurrentWord = () => {
    const el = inputRef.current;
    if (!el) return '';
    const text = el.value;
    const cur = el.selectionStart;
    let start = cur - 1;
    while (start >= 0 && !/[\s\n]/.test(text[start])) start--;
    start++;
    let end = cur;
    while (end < text.length && !/[\s\n]/.test(text[end])) end++;
    return text.slice(start, end);
  };

  const fetchSuggestions = async (word) => {
    if (!word || word === lastFetchedWord.current) return;
    lastFetchedWord.current = word;
    try {
      const url = `https://inputtools.google.com/request?text=${encodeURIComponent(word)}&itc=hi-t-i0-und&num=5&cp=0&cs=1&ie=utf-8&oe=utf-8`;
      const res = await fetch(url);
      const data = await res.json();
      if (data[0] === 'SUCCESS' && data[1]?.[0]?.[1]?.length) {
        setSuggestions({ roman: word, list: data[1][0][1] });
        return;
      }
    } catch (_) { /* fallback */ }

    const main = transliterateWord(word);
    const alts = new Set([main]);
    alts.add(transliterateWord(word.replace(/a$/, 'aa')));
    alts.add(transliterateWord(word.replace(/i$/, 'ii')));
    const list = [...alts].filter(Boolean).slice(0, 4);
    setSuggestions({ roman: word, list });
  };

  const acceptSuggestion = (roman, hindi) => {
    wordOverrides.current.set(roman.toLowerCase(), hindi);
    setOutputHtml(buildOutputHtml(inputRef.current?.value || '', wordOverrides.current));
    setSuggestions(null);
    lastFetchedWord.current = '';
    inputRef.current?.focus();
  };

  const handleInput = (e) => {
    const text = e.target.value;
    setInputText(text);

    if (!startTime.current && text.trim()) {
      startTime.current = Date.now();
      statsInterval.current = setInterval(() => updateStats(inputRef.current?.value || ''), 1000);
    }

    setOutputHtml(buildOutputHtml(text, wordOverrides.current));
    updateStats(text);

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      const word = getCurrentWord();
      if (word.length >= 2) fetchSuggestions(word);
      else { setSuggestions(null); lastFetchedWord.current = ''; }
    }, 280);
  };

  const handleKeyDown = (e) => {
    if (suggestions && e.key >= '1' && e.key <= '5') {
      const idx = +e.key - 1;
      if (suggestions.list[idx]) {
        e.preventDefault();
        acceptSuggestion(suggestions.roman, suggestions.list[idx]);
      }
    }
    if (e.key === 'Escape') { setSuggestions(null); lastFetchedWord.current = ''; }
  };

  const handleBlur = () => {
    setTimeout(() => { setSuggestions(null); lastFetchedWord.current = ''; }, 200);
  };

  const handleClick = () => {
    const word = getCurrentWord();
    if (word.length >= 2) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => fetchSuggestions(word), 150);
    } else {
      setSuggestions(null);
      lastFetchedWord.current = '';
    }
  };

  const handleCopy = async () => {
    const text = (outputRef.current?.innerText || '').replace(/Hindi will appear here…/, '').trim();
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopyLabel('Copied!');
    setTimeout(() => setCopyLabel('Copy'), 1500);
  };

  const handleClear = () => {
    setInputText('');
    setOutputHtml('<span class="ht-placeholder">Hindi will appear here…</span>');
    setSuggestions(null);
    wordOverrides.current.clear();
    startTime.current = null;
    clearInterval(statsInterval.current);
    setCharCount(0);
    setWordCount(0);
    setTimeElapsed('0s');
    setWpm(0);
    lastFetchedWord.current = '';
    inputRef.current?.focus();
  };

  return (
    <div className="ht-page">
      <Helmet>
        <title>Hindi Typing Online – English to Hindi Transliteration | AStudyHub</title>
        <meta name="description" content="Free online Hindi typing tool. Type in English (Roman) and instantly convert to Hindi (Devanagari) script. No software needed – just type and get Hindi text." />
        <meta name="keywords" content="hindi typing, hindi typing online, online hindi typing, english to hindi typing, roman to hindi, hindi transliteration, hindi typing tool, hindi type karne ka tarika, हिंदी टाइपिंग, hindi likhna, devanagari typing" />
        <link rel="canonical" href="https://www.astudyhub.org/hindi-typing" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.astudyhub.org/hindi-typing" />
        <meta property="og:title" content="Hindi Typing Online – English to Hindi Transliteration | AStudyHub" />
        <meta property="og:description" content="Free online Hindi typing tool. Type in English (Roman) and instantly convert to Hindi (Devanagari) script. No software needed." />
        <meta property="og:image" content="https://www.astudyhub.org/logo512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Hindi Typing Online – English to Hindi | AStudyHub" />
        <meta name="twitter:description" content="Free online Hindi typing tool. Type in English and instantly get Hindi (Devanagari) text." />
        <meta name="twitter:image" content="https://www.astudyhub.org/logo512.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Hindi Typing",
            "url": "https://www.astudyhub.org/hindi-typing",
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "Any",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
            "description": "Free online Hindi typing tool. Type in English (Roman) and instantly convert to Hindi (Devanagari) script.",
            "isPartOf": { "@type": "WebSite", "name": "AStudyHub", "url": "https://www.astudyhub.org" }
          })}
        </script>
      </Helmet>
      <style>{`
        .ht-page {
          font-family: 'Roboto', sans-serif;
          background: #f8f9fa;
          min-height: 60vh;
          margin-top: 136px;
          padding: 32px 24px 48px;
        }
        .ht-hero { margin-bottom: 28px; }
        .ht-hero h1 {
          font-size: 1.8rem;
          font-weight: 400;
          color: #202124;
          margin-bottom: 6px;
          display: flex;
          align-items: baseline;
          gap: 12px;
          flex-wrap: wrap;
        }
        .ht-hero h1 .ht-devanagari {
          font-family: 'Noto Sans Devanagari', sans-serif;
          font-size: 1.5rem;
          color: #1c7049;
          font-weight: 500;
        }
        .ht-hero p { color: #5f6368; font-size: .95rem; }
        .ht-editor-wrap {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          border: 1px solid #c2d9cc;
          border-radius: 8px;
          overflow: hidden;
          background: #fff;
          box-shadow: 0 1px 3px rgba(28,112,73,.15), 0 4px 8px rgba(28,112,73,.1);
          margin-bottom: 20px;
          max-width: 1000px;
          margin-left: auto;
          margin-right: auto;
        }
        .ht-panel { display: flex; flex-direction: column; min-height: 320px; }
        .ht-panel-input { border-right: 1px solid #c2d9cc; }
        .ht-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 16px;
          border-bottom: 1px solid #c2d9cc;
          background: #f4faf7;
        }
        .ht-panel-label {
          font-size: .78rem;
          font-weight: 500;
          color: #1c7049;
          text-transform: uppercase;
          letter-spacing: .6px;
        }
        .ht-typing-area {
          flex: 1;
          width: 100%;
          padding: 16px;
          border: none;
          outline: none;
          resize: none;
          font-family: 'Roboto', sans-serif;
          font-size: 1.05rem;
          line-height: 1.7;
          color: #202124;
          background: transparent;
          min-height: 220px;
        }
        .ht-typing-area::placeholder { color: #a8c4b5; }
        .ht-suggestions {
          padding: 10px 14px;
          background: #e8f5ee;
          border-top: 1px solid #a8d5bc;
          font-size: .88rem;
        }
        .ht-sug-label { color: #4a7a62; font-size: .8rem; display: block; margin-bottom: 6px; }
        .ht-sug-label strong { color: #202124; }
        .ht-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 6px; }
        .ht-chip {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 5px 12px;
          border-radius: 18px;
          border: 1.5px solid #1c7049;
          background: #fff;
          color: #1c7049;
          font-family: 'Noto Sans Devanagari', sans-serif;
          font-size: 1rem;
          cursor: pointer;
          transition: background .15s, color .15s;
        }
        .ht-chip:hover { background: #1c7049; color: #fff; }
        .ht-chip-num { font-family: 'Roboto', sans-serif; font-size: .7rem; font-weight: 700; opacity: .7; min-width: 12px; }
        .ht-chip-text { font-size: 1rem; }
        .ht-sug-hint { color: #4a7a62; font-size: .75rem; }
        .ht-output-area {
          flex: 1;
          padding: 16px;
          font-family: 'Noto Sans Devanagari', sans-serif;
          font-size: 1.1rem;
          line-height: 1.8;
          color: #202124;
          min-height: 220px;
          word-break: break-word;
          white-space: pre-wrap;
        }
        .ht-placeholder { color: #a8c4b5; font-family: 'Roboto', sans-serif; font-size: 1rem; }
        .ht-word { display: inline; border-radius: 3px; padding: 0 1px; }
        .ht-btn-ghost {
          padding: 4px 12px;
          border: 1px solid #c2d9cc;
          border-radius: 4px;
          background: transparent;
          color: #1c7049;
          font-size: .8rem;
          font-weight: 500;
          cursor: pointer;
          transition: background .15s, color .15s, border-color .15s;
        }
        .ht-btn-ghost:hover { background: #1c7049; color: #fff; border-color: #1c7049; }
        .ht-stats-bar {
          display: flex;
          border: 1px solid #c2d9cc;
          border-radius: 8px;
          background: #fff;
          overflow: hidden;
          margin-bottom: 24px;
          max-width: 1000px;
          margin-left: auto;
          margin-right: auto;
        }
        .ht-stat {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 14px 8px;
          border-right: 1px solid #c2d9cc;
        }
        .ht-stat:last-child { border-right: none; }
        .ht-stat-label { font-size: .72rem; font-weight: 500; text-transform: uppercase; letter-spacing: .5px; color: #4a7a62; margin-bottom: 4px; }
        .ht-stat-val { font-size: 1.4rem; font-weight: 400; color: #1c7049; letter-spacing: -.5px; }
        .ht-reference {
          border: 1px solid #c2d9cc;
          border-radius: 8px;
          background: #fff;
          overflow: hidden;
          max-width: 1000px;
          margin-left: auto;
          margin-right: auto;
        }
        .ht-reference summary {
          padding: 14px 20px;
          font-size: .9rem;
          font-weight: 500;
          cursor: pointer;
          color: #4a7a62;
          list-style: none;
          display: flex;
          align-items: center;
          user-select: none;
        }
        .ht-reference summary::-webkit-details-marker { display: none; }
        .ht-reference summary::before { content: '▶'; font-size: .65rem; margin-right: 8px; transition: transform .2s; }
        .ht-reference[open] summary::before { transform: rotate(90deg); }
        .ht-reference summary:hover { background: #f4faf7; color: #1c7049; }
        .ht-ref-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 24px;
          padding: 20px;
          border-top: 1px solid #c2d9cc;
        }
        .ht-ref-group h4 { font-size: .8rem; text-transform: uppercase; letter-spacing: .5px; color: #1c7049; margin-bottom: 10px; font-weight: 500; }
        .ht-ref-group table { width: 100%; border-collapse: collapse; font-size: .88rem; }
        .ht-ref-group td { padding: 4px 8px; border-bottom: 1px solid #eaf4ef; color: #202124; }
        .ht-ref-group td:first-child { color: #4a7a62; width: 30%; }
        .ht-ref-group td:nth-child(3) { color: #4a7a62; }
        .ht-ref-group code { font-family: 'Roboto Mono', 'Courier New', monospace; font-size: .82rem; background: #eaf4ef; padding: 1px 5px; border-radius: 3px; color: #1c7049; }
        .ht-ref-group td:nth-child(even) { font-family: 'Noto Sans Devanagari', sans-serif; font-size: .95rem; }
        .ht-lang-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #e8f5ee;
          border-radius: 20px;
          padding: 4px 14px;
          font-size: .85rem;
          font-weight: 500;
          color: #1c7049;
          margin-bottom: 12px;
        }
        .ht-lang-pill .ht-arrow { color: #4a7a62; }
        @media (max-width: 680px) {
          .ht-page { padding: 20px 16px 40px; }
          .ht-editor-wrap { grid-template-columns: 1fr; }
          .ht-panel-input { border-right: none; border-bottom: 1px solid #c2d9cc; }
          .ht-hero h1 { font-size: 1.4rem; }
          .ht-stats-bar { flex-wrap: wrap; }
          .ht-stat { flex: 1 1 calc(50% - 1px); }
          .ht-stat:nth-child(2) { border-right: none; }
          .ht-ref-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <div className="ht-hero">
          <div className="ht-lang-pill">
            <span>English</span>
            <span className="ht-arrow">→</span>
            <span>हिंदी</span>
          </div>
          <h1>Hindi Typing <span className="ht-devanagari">हिंदी टाइपिंग</span></h1>
          <p>Type in Roman (English) and see it convert to Hindi script instantly.</p>
        </div>

        <div className="ht-editor-wrap">
          {/* Input panel */}
          <div className="ht-panel ht-panel-input">
            <div className="ht-panel-header">
              <span className="ht-panel-label">Roman (English)</span>
              <div>
                <button className="ht-btn-ghost" onClick={handleClear}>Clear</button>
              </div>
            </div>
            <textarea
              ref={inputRef}
              className="ht-typing-area"
              value={inputText}
              placeholder="Type here… e.g. namaste, kaise ho aap"
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onClick={handleClick}
            />
            {suggestions && suggestions.list.length > 0 && (
              <div className="ht-suggestions">
                <span className="ht-sug-label">Suggestions for <strong>"{suggestions.roman}"</strong>:</span>
                <div className="ht-chips">
                  {suggestions.list.map((hindi, idx) => (
                    <button
                      key={idx}
                      className="ht-chip"
                      onMouseDown={(e) => { e.preventDefault(); acceptSuggestion(suggestions.roman, hindi); }}
                    >
                      <span className="ht-chip-num">{idx + 1}</span>
                      <span className="ht-chip-text">{hindi}</span>
                    </button>
                  ))}
                </div>
                <span className="ht-sug-hint">Click or press 1–5 to select</span>
              </div>
            )}
          </div>

          {/* Output panel */}
          <div className="ht-panel">
            <div className="ht-panel-header">
              <span className="ht-panel-label">हिंदी (Hindi)</span>
              <div>
                <button className="ht-btn-ghost" onClick={handleCopy}>{copyLabel}</button>
              </div>
            </div>
            <div
              ref={outputRef}
              className="ht-output-area"
              dangerouslySetInnerHTML={{ __html: outputHtml }}
            />
          </div>
        </div>

        {/* Stats bar */}
        <div className="ht-stats-bar">
          <div className="ht-stat"><span className="ht-stat-label">Characters</span><span className="ht-stat-val">{charCount}</span></div>
          <div className="ht-stat"><span className="ht-stat-label">Words</span><span className="ht-stat-val">{wordCount}</span></div>
          <div className="ht-stat"><span className="ht-stat-label">Time</span><span className="ht-stat-val">{timeElapsed}</span></div>
          <div className="ht-stat"><span className="ht-stat-label">WPM</span><span className="ht-stat-val">{wpm}</span></div>
        </div>

        {/* Quick reference */}
        <details className="ht-reference">
          <summary>Quick Reference – Typing Guide</summary>
          <div className="ht-ref-grid">
            <div className="ht-ref-group">
              <h4>Vowels</h4>
              <table>
                <tbody>
                  <tr><td><code>a</code></td><td>अ</td><td><code>aa / A</code></td><td>आ</td></tr>
                  <tr><td><code>i</code></td><td>इ</td><td><code>ii / I / ee</code></td><td>ई</td></tr>
                  <tr><td><code>u</code></td><td>उ</td><td><code>uu / U / oo</code></td><td>ऊ</td></tr>
                  <tr><td><code>e</code></td><td>ए</td><td><code>ai</code></td><td>ऐ</td></tr>
                  <tr><td><code>o</code></td><td>ओ</td><td><code>au / aw</code></td><td>औ</td></tr>
                  <tr><td><code>ri</code></td><td>ऋ</td><td><code>M</code></td><td>ं (anusvara)</td></tr>
                </tbody>
              </table>
            </div>
            <div className="ht-ref-group">
              <h4>Consonant Clusters</h4>
              <table>
                <tbody>
                  <tr><td><code>kh</code></td><td>ख</td><td><code>gh</code></td><td>घ</td></tr>
                  <tr><td><code>ch</code></td><td>च</td><td><code>chh</code></td><td>छ</td></tr>
                  <tr><td><code>jh</code></td><td>झ</td><td><code>th</code></td><td>थ</td></tr>
                  <tr><td><code>dh</code></td><td>ध</td><td><code>ph</code></td><td>फ</td></tr>
                  <tr><td><code>bh</code></td><td>भ</td><td><code>sh</code></td><td>श</td></tr>
                  <tr><td><code>Th</code></td><td>ठ</td><td><code>Dh</code></td><td>ढ</td></tr>
                  <tr><td><code>ksh</code></td><td>क्ष</td><td><code>gya</code></td><td>ज्ञ</td></tr>
                </tbody>
              </table>
            </div>
            <div className="ht-ref-group">
              <h4>Examples</h4>
              <table>
                <tbody>
                  <tr><td><code>namaste</code></td><td>नमस्ते</td></tr>
                  <tr><td><code>kaise ho</code></td><td>कैसे हो</td></tr>
                  <tr><td><code>bharat</code></td><td>भरत</td></tr>
                  <tr><td><code>bhaarat</code></td><td>भारत</td></tr>
                  <tr><td><code>hindi</code></td><td>हिन्दि</td></tr>
                  <tr><td><code>hindii</code></td><td>हिन्दी</td></tr>
                  <tr><td><code>shakti</code></td><td>शक्ति</td></tr>
                  <tr><td><code>dhanyavaad</code></td><td>धन्यवाद</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
};

export default HindiTypingPage;
